import React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import CustomSelect, { SelectOption } from 'components/form/CustomSelect';
import DateTimePicker from 'components/form/DateTimePicker';
import KatakanaTextField from 'components/form/KatakanaTextField';

// --- 型定義 ---

export type TemplateSelectItem = {
  code: string;
  name: string;
};

type TemplateFormFieldProps = {
  /** フォーム種別 (FormType) */
  formType: string;
  /** ラベル */
  label: string;
  /** 現在の値 (string / string[] をJSON stringified) */
  value: string;
  /** 値変更ハンドラ */
  onChange: (value: string) => void;
  /** 選択肢リスト (SELECT系の場合に必要) */
  selectItems?: TemplateSelectItem[];
  /** 無効状態 */
  disabled?: boolean;
};

/**
 * テンプレート項目のフォーム種別に応じた入力コンポーネントをレンダリングする。
 *
 * 対応する FormType:
 * - TEXT: テキスト入力
 * - KANA_TEXT: カタカナ入力 (KatakanaTextField)
 * - NUMBER_TEXT: 数値入力
 * - SELECT: 単一選択 (CustomSelect)
 * - MULTI_SELECT: 複数選択 (CustomSelect)
 * - RADIO: ラジオボタン
 * - CHECK: 単一チェックボックス
 * - MULTI_CHECK: 複数チェックボックス
 * - MULTI_CHECK_BUTTON: ボタン風チェック
 * - DATE_PICKER_START: 日付ピッカー（開始）
 * - DATE_PICKER_END: 日付ピッカー（終了）
 * - TECH_STACK_SELECT: 技術スタック選択（テキスト入力で代替）
 */
const TemplateFormField: React.FC<TemplateFormFieldProps> = ({
  formType,
  label,
  value,
  onChange,
  selectItems = [],
  disabled = false,
}) => {
  const options: SelectOption[] = selectItems.map((si) => ({
    value: si.code,
    label: si.name,
  }));

  switch (formType) {
    case 'TEXT':
      return (
        <TextField
          size="small"
          fullWidth
          placeholder={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      );

    case 'KANA_TEXT':
      return (
        <KatakanaTextField
          size="small"
          fullWidth
          placeholder={label}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'NUMBER_TEXT':
      return (
        <TextField
          size="small"
          fullWidth
          placeholder={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          type="number"
          disabled={disabled}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      );

    case 'SELECT':
      return (
        <CustomSelect
          mode="single"
          options={options}
          value={value}
          onChange={onChange}
          placeholder={label}
          disabled={disabled}
        />
      );

    case 'MULTI_SELECT':
      return (
        <CustomSelect
          mode="multi"
          options={options}
          value={parseMultiValue(value)}
          onChange={(vals) => onChange(JSON.stringify(vals))}
          placeholder={label}
          disabled={disabled}
        />
      );

    case 'RADIO':
      return (
        <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
          {selectItems.map((si) => (
            <FormControlLabel
              key={si.code}
              value={si.code}
              control={<Radio size="small" disabled={disabled} />}
              label={si.name}
              sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}
            />
          ))}
        </RadioGroup>
      );

    case 'CHECK':
      return (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={value === 'true'}
              onChange={(e) => onChange(String(e.target.checked))}
              disabled={disabled}
            />
          }
          label={label}
          sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}
        />
      );

    case 'MULTI_CHECK': {
      const checked = parseMultiValue(value);
      return (
        <Stack direction="row" flexWrap="wrap" spacing={0}>
          {selectItems.map((si) => (
            <FormControlLabel
              key={si.code}
              control={
                <Checkbox
                  size="small"
                  checked={checked.includes(si.code)}
                  onChange={() => {
                    const next = checked.includes(si.code)
                      ? checked.filter((v) => v !== si.code)
                      : [...checked, si.code];
                    onChange(JSON.stringify(next));
                  }}
                  disabled={disabled}
                />
              }
              label={si.name}
              sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}
            />
          ))}
        </Stack>
      );
    }

    case 'MULTI_CHECK_BUTTON': {
      const checkedBtn = parseMultiValue(value);
      return (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {selectItems.map((si) => {
            const isActive = checkedBtn.includes(si.code);
            return (
              <Button
                key={si.code}
                size="small"
                variant={isActive ? 'contained' : 'outlined'}
                onClick={() => {
                  if (disabled) return;
                  const next = isActive
                    ? checkedBtn.filter((v) => v !== si.code)
                    : [...checkedBtn, si.code];
                  onChange(JSON.stringify(next));
                }}
                disabled={disabled}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                {si.name}
              </Button>
            );
          })}
        </Stack>
      );
    }

    case 'DATE_PICKER_START':
      return (
        <DateTimePicker
          mode="start"
          value={value}
          onChange={onChange}
          label={label}
          fullWidth
          size="small"
          disabled={disabled}
        />
      );

    case 'DATE_PICKER_END':
      return (
        <DateTimePicker
          mode="end"
          value={value}
          onChange={onChange}
          label={label}
          fullWidth
          size="small"
          disabled={disabled}
        />
      );

    case 'TECH_STACK_SELECT':
      return (
        <Box>
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ minHeight: 32 }}>
            {parseMultiValue(value).map((v) => (
              <Chip
                key={v}
                label={v}
                size="small"
                onDelete={
                  disabled
                    ? undefined
                    : () => {
                        const next = parseMultiValue(value).filter((x) => x !== v);
                        onChange(JSON.stringify(next));
                      }
                }
                sx={{ borderRadius: 1.5 }}
              />
            ))}
          </Stack>
          {!disabled && (
            <TextField
              size="small"
              fullWidth
              placeholder="技術スタックを入力して Enter"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const input = (e.target as HTMLInputElement).value.trim();
                  if (input) {
                    const current = parseMultiValue(value);
                    if (!current.includes(input)) {
                      onChange(JSON.stringify([...current, input]));
                    }
                    (e.target as HTMLInputElement).value = '';
                  }
                }
              }}
              sx={{ mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}
        </Box>
      );

    default:
      // 未対応の formType はテキスト入力にフォールバック
      return (
        <TextField
          size="small"
          fullWidth
          placeholder={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      );
  }
};

/** JSON配列文字列 or 空文字を string[] にパース */
const parseMultiValue = (value: string): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default TemplateFormField;
